import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import LockIcon from '@material-ui/icons/Lock';
import { Notification, useTranslate, useLogin, useNotify } from 'react-admin';
import { lightTheme } from './themes';
import GetIP from '../controls/GetIP'

const { REACT_APP_BACKGROUND_IMAGE_URL } = process.env;
const { REACT_APP_SITE_NAME } = process.env || '';
const useStyles = makeStyles(theme => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: `linear-gradient(135deg, rgba(40,53,147,0.72) 0%, rgba(0,16,100,0.78) 100%), url(${REACT_APP_BACKGROUND_IMAGE_URL})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    },
    card: {
        minWidth: 340,
        maxWidth: 380,
        marginTop: '8em',
        borderRadius: 16,
        paddingBottom: '0.5em',
        boxShadow:
            '0 10px 40px rgba(0,16,100,0.35), 0 2px 8px rgba(0,16,100,0.2)',
        backgroundColor: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(6px)',
    },
    avatar: {
        margin: '1.5em 1em 0.5em 1em',
        display: 'flex',
        justifyContent: 'center',
    },
    icon: {
        width: 56,
        height: 56,
        backgroundImage: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
        boxShadow: '0 6px 18px rgba(40,53,147,0.4)',
    },
    hint: {
        marginTop: '0.25em',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'center',
        gap: '0.4em',
        color: theme.palette.secondary.main,
    },
    brandTitle: {
        margin: 0,
        fontSize: '1.9rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
        color: theme.palette.secondary.dark,
    },
    brandSub: {
        margin: 0,
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.palette.secondary.main,
    },
    siteName: {
        marginTop: '0.5em',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[500],
        fontSize: '0.85rem',
    },
    form: {
        padding: '0 1.5em 1em 1.5em',
    },
    input: {
        marginTop: '1em',
    },
    actions: {
        padding: '0.5em 1.5em 1.5em 1.5em',
    },
    button: {
        borderRadius: 10,
        padding: '0.6em 0',
        fontWeight: 600,
        boxShadow: '0 6px 16px rgba(40,53,147,0.3)',
    },
}));

const renderInput = ({
    meta: { touched, error } = {},
    input: { ...inputProps },
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />
);

const Login = () => {
    const [loading, setLoading] = useState(false);
    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();
    const login = useLogin();

    const handleSubmit = auth => {
        setLoading(true);
       // login(auth, location.state ? location.state.nextPathname : '/')
       login(auth, '/')
            .then(() => setLoading(false))
            .catch(error => {
                setLoading(false);
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                        ? 'ra.auth.sign_in_error'
                        : error.message,
                    'warning'
                );
            });
    };

    const validate = values => {
        const errors = {};
        if (!values.username) {
            errors.username = translate('ra.validation.required');
        }
        if (!values.password) {
            errors.password = translate('ra.validation.required');
        }
        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={{ ipaddress: GetIP() }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} noValidate>
                    <div className={classes.main}>
                        <Card className={classes.card}>
                            <div className={classes.avatar}>
                                <Avatar className={classes.icon}>
                                    <LockIcon />
                                </Avatar>
                            </div>
                            <div className={classes.hint}>
                                <h1 className={classes.brandTitle}><i>HemDonER</i></h1>
                                <h4 className={classes.brandSub}><i>e<b>DMS</b></i></h4>
                            </div>
                            <div className={classes.form}>
                                <div className={classes.input}>
                                    <Field
                                        autoFocus
                                        name="username"
                                        component={renderInput}
                                        label={translate('ra.auth.username')}
                                        disabled={loading}
                                    />
                                </div>
                                <div className={classes.input}>
                                    <Field
                                        name="password"
                                        component={renderInput}
                                        label={translate('ra.auth.password')}
                                        type="password"
                                        disabled={loading}
                                    />
                                </div>
                                <div className={classes.input} style={{visibility: "hidden"}}>
                                    <Field
                                        name="ipaddress"
                                        component={renderInput}
                                        disabled={true}
                                        type = "hidden"
                                    />
                                </div>
                            </div>
                            <CardActions className={classes.actions}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={loading}
                                    className={classes.button}
                                    fullWidth
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={25}
                                            thickness={2}
                                        />
                                    )}
                                    {translate('ra.auth.sign_in')}
                                </Button>
                            </CardActions>
                            {REACT_APP_SITE_NAME && REACT_APP_SITE_NAME !== '' && <div className={classes.siteName}>
                                <p>{REACT_APP_SITE_NAME}</p>
                            </div>}
                        </Card>
                        <Notification />
                        
                    </div>
                </form>
            )}
        />
    );
};

Login.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in Login won't get
// the right theme
const LoginWithTheme = props => (
    <ThemeProvider theme={createTheme(lightTheme)}>
        <Login {...props} />
    </ThemeProvider>
);

export default LoginWithTheme;